import { useState, useEffect, useCallback } from 'react';
import { Button, notification } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CustomerServiceOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G } from '../shared/constants';
import { authHeader, useLang } from '../shared/utils';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export function InquiryTab() {
  const [form,     setForm]     = useState({ subject: '', message: '', inquiry_type: 'General' });
  const [sending,  setSending]  = useState(false);
  const [myItems,  setMyItems]  = useState([]);
  const [view,     setView]     = useState('form'); // 'form' | 'history'

  const fetchMine = useCallback(async () => {
    axios.get(`${baseUrl}/member/my-inquiries`, { headers: authHeader() })
      .then(r => { if (r.data.status) setMyItems(r.data.result || []); })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchMine(); }, [fetchMine]);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      notification.warning({ message: 'विषय और संदेश आवश्यक हैं', placement: 'topRight' });
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${baseUrl}/member/inquiry`, form, { headers: authHeader() });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: 'topRight' });
        setForm({ subject: '', message: '', inquiry_type: 'General' });
        fetchMine();
        setView('history');
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Failed', placement: 'topRight' });
    } finally { setSending(false); }
  };

  const inputStyle = { width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const statusColor  = { Pending: '#B45309', pending: '#B45309', Completed: '#15803D', resolved: '#15803D', closed: '#6b7280', InProcess: '#1D4ED8' };
  const statusLabel  = { Pending: 'Pending', pending: 'Pending', Completed: '✅ हल हो गया', resolved: '✅ हल हो गया', InProcess: '🔄 प्रक्रिया में' };

  return (
    <div>
      {/* Toggle — pill style matching Image #24 */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: '#f3f4f6', borderRadius: 40, padding: 5, width: 'fit-content' }}>
        {[
          ['form',    <CustomerServiceOutlined style={{ fontSize: 14 }} />, 'सहायता अनुरोध'],
          ['history', <FileTextOutlined        style={{ fontSize: 14 }} />, 'मेरी Inquiries'],
        ].map(([k, icon, l]) => (
          <button key={k} onClick={() => setView(k)} style={{
            fontSize: 12, fontWeight: 800, padding: '7px 18px', borderRadius: 30,
            cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
            background: view === k ? M : 'transparent',
            color: view === k ? G : '#6b7280',
            display: 'flex', alignItems: 'center', gap: 7,
            boxShadow: view === k ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            transition: 'all 0.18s',
          }}>{icon}{l}</button>
        ))}
      </div>

      {view === 'form' && (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: M, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7 }}><CustomerServiceOutlined /> सहायता / शिकायत / सुझाव</div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5 }}>प्रकार</div>
            <select value={form.inquiry_type} onChange={e => setForm(f => ({ ...f, inquiry_type: e.target.value }))} style={{ ...inputStyle, height: 38 }}>
              {['General', 'Payment Issue', 'Account Problem', 'Scholarship', 'Grievance', 'Suggestion'].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5 }}>विषय *</div>
            <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="संक्षिप्त विषय लिखें" style={{ ...inputStyle, height: 38 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5 }}>विवरण *</div>
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4} placeholder="अपनी समस्या / सुझाव विस्तार से लिखें…" style={inputStyle} />
          </div>
          <Button block loading={sending} onClick={submit} style={{ height: 44, borderRadius: 10, fontWeight: 700, fontSize: 14, background: M, borderColor: M, color: G }}>
            भेजें 📤
          </Button>
        </div>
      )}

      {view === 'history' && (
        <div>
          {!myItems.length ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
              <div>अभी कोई inquiry नहीं है</div>
            </div>
          ) : myItems.map(item => (
            <div key={item.id} style={{
              background: '#fff', borderRadius: 12, marginBottom: 12, overflow: 'hidden',
              border: item.admin_reply ? '1px solid #86efac' : '1px solid #e5e7eb',
              boxShadow: item.admin_reply ? '0 2px 8px rgba(21,128,61,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
            }}>
              {/* Header bar */}
              <div style={{ padding: '10px 14px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  {item.inquiry_type && (
                    <span style={{ fontSize: 9, fontWeight: 700, background: '#f3f4f6', color: '#6b7280', borderRadius: 20, padding: '2px 8px' }}>
                      {item.inquiry_type}
                    </span>
                  )}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 10px', flexShrink: 0,
                  color: statusColor[item.status] || '#6b7280',
                  background: (item.status === 'Completed' || item.status === 'resolved') ? '#f0fdf4' : (item.status === 'InProcess') ? '#eff6ff' : '#fef3c7',
                }}>
                  {statusLabel[item.status] || item.status}
                </span>
              </div>

              {/* Inquiry content */}
              <div style={{ padding: '10px 14px' }}>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{item.subject}</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>
                  {item.createdAt ? new Date(item.createdAt).toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                </div>
              </div>

              {/* Admin reply */}
              {item.admin_reply ? (
                <div style={{ background: '#f0fdf4', borderTop: '1px solid #86efac', padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#15803D', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <CheckCircleOutlined /> ABRSVS का जवाब
                  </div>
                  <div style={{ fontSize: 13, color: '#166534', lineHeight: 1.6 }}>{item.admin_reply}</div>
                </div>
              ) : (
                <div style={{ background: '#fafafa', borderTop: '1px solid #f3f4f6', padding: '8px 14px' }}>
                  <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <ClockCircleOutlined style={{ fontSize: 10 }} /> जवाब का इंतजार है…
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Referral Tab (सदस्य जोड़ें) ──────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Samajik Yogdan — Social Contributions Tab
